import React, { useRef, useState } from "react";
import { CircularProgress, Tooltip } from "@mui/material";
import {
  DocumentTextIcon,
  ArrowUpTrayIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import {
  useApiRequirements,
  useUploadApiRequirement,
  useDeleteApiRequirement,
} from "../../../useQuery/useApiRequirements";

/**
 * Requirements documents attached to a collection.
 *
 * Whatever is attached here is used automatically the next time this collection generates test
 * cases or chains - there is nothing else to wire up per generation run.
 */

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const RequirementsPanel = ({ collectionId, companyId }) => {
  const fileInput = useRef(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const { data, isLoading, isError, error } = useApiRequirements(collectionId, companyId);
  const upload = useUploadApiRequirement(collectionId, companyId, {
    onSuccess: (result) => toast.success(result.message || "Requirements attached"),
    onError: (e) => toast.error(e?.response?.data?.message || "Could not attach the document"),
  });
  const del = useDeleteApiRequirement(collectionId, companyId, {
    onSuccess: () => toast.success("Requirement removed"),
    onError: (e) => toast.error(e?.response?.data?.message || "Could not remove the document"),
    onSettled: () => setPendingDelete(null),
  });

  const handlePicked = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) upload.mutate({ file, name: file.name });
  };

  if (!collectionId) return null;

  const requirements = data?.requirements || [];

  return (
    <div className="flex flex-col gap-3 font-apple">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[13px] font-semibold text-gray-800">Requirements</h3>
          <p className="text-[12px] text-gray-500 mt-0.5">
            What the generated test cases and chains for this collection must satisfy - a spec,
            an integration guide, or domain rules. Used automatically on every future generation.
          </p>
        </div>
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={upload.isPending}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded cursor-pointer flex-shrink-0"
        >
          {upload.isPending ? (
            <CircularProgress size={14} sx={{ color: "white" }} />
          ) : (
            <ArrowUpTrayIcon className="h-4 w-4" />
          )}
          Upload
        </button>
        <input
          ref={fileInput}
          type="file"
          accept=".pdf,.docx,.txt,.md,.json,.yaml,.yml,.csv"
          onChange={handlePicked}
          style={{ display: "none" }}
        />
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-[13px] text-gray-500 py-3">
          <CircularProgress size={14} /> Loading…
        </div>
      )}

      {isError && (
        <div className="text-[13px] text-red-700">
          {error?.response?.data?.message || "Could not load requirements."}
        </div>
      )}

      {!isLoading && !isError && requirements.length === 0 && (
        <div className="text-[13px] text-gray-500 border border-dashed border-gray-300 rounded px-3 py-4 text-center">
          No requirements attached yet. Upload a PDF, Word document, or text file.
        </div>
      )}

      {requirements.length > 0 && (
        <div className="flex flex-col gap-2">
          {requirements.map((req) => (
            <div
              key={req.id}
              className="flex items-start gap-2.5 border border-gray-200 rounded px-3 py-2.5"
            >
              <DocumentTextIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-gray-800 truncate">{req.name}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">
                  {req.fileName !== req.name && <span className="mr-2">{req.fileName}</span>}
                  {req.charCount?.toLocaleString()} characters
                  {req.uploadedAt && <span> · added {formatDate(req.uploadedAt)}</span>}
                </div>
                {req.preview && (
                  <p className="text-[12px] text-gray-500 mt-1 line-clamp-2">{req.preview}</p>
                )}
              </div>
              <Tooltip title="Remove">
                <button
                  type="button"
                  onClick={() => setPendingDelete(req)}
                  className="flex-shrink-0 p-1 text-gray-400 hover:text-red-600 cursor-pointer"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </Tooltip>
            </div>
          ))}
        </div>
      )}

      {pendingDelete && (
        <div className="flex items-start gap-2 border border-amber-200 bg-amber-50 rounded px-3 py-2.5">
          <ExclamationTriangleIcon className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1 text-[13px] text-amber-900">
            Remove "{pendingDelete.name}"? Future generations will no longer be checked against it.
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => del.mutate(pendingDelete.id)}
                disabled={del.isPending}
                className="px-2.5 py-1 text-[12px] font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded cursor-pointer"
              >
                {del.isPending ? "Removing…" : "Remove"}
              </button>
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                className="px-2.5 py-1 text-[12px] font-medium text-gray-700 hover:bg-gray-100 rounded cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequirementsPanel;
